import React, { useCallback, memo } from 'react';
import { pick } from 'ramda';
import { useTranslations } from 'js/hooks/i18n';
import { Icon } from 'js/components/common/Icon';
import { clsb, clsx } from 'js/utils/classNames.util';

import * as Types from '../../types';
import * as actions from '../../store/actions';
import { useDynamicReducerSelector, useDynamicReducerDispatch } from '../../store';

import style from './CarouselControls.scss';

export const CarouselControlsBare: React.FC<Types.ICarouselControlsProps> = ({ classNames, setFocusedItem }) => {
    const dispatch = useDynamicReducerDispatch();
    const translate = useTranslations('playtech.mobile.system.carousel.');
    const { activeSlide, slidesCount, config: { loop, itemsPerView } } = useDynamicReducerSelector(pick([
        'activeSlide', 'slidesCount', 'config'
    ]));

    const isPrevControlActive = loop || activeSlide !== 0;
    const isNextControlActive = loop || activeSlide < slidesCount - itemsPerView;

    const prevControlClassName = clsx([
        clsb([style, classNames], ['control', 'prevControl']),
        !isPrevControlActive ? clsb([style, classNames], ['disabledControl']) : '',
        'cms-carousel-controls-prev'
    ]);

    const nextControlClassName = clsx([
        clsb([style, classNames], ['control', 'nextControl']),
        !isNextControlActive ? clsb([style, classNames], ['disabledControl']) : '',
        'cms-carousel-controls-next'
    ]);

    const showPrevSlide = useCallback(() => {
        setFocusedItem(null);
        dispatch(actions.moveLeft(1));
    }, [dispatch, setFocusedItem]);

    const showNextSlide = useCallback(() => {
        setFocusedItem(null);
        dispatch(actions.moveRight(1));
    }, [dispatch, setFocusedItem]);

    return (
        <>
            <Icon
                role="button"
                className={prevControlClassName}
                {...isPrevControlActive && {
                    onClick: showPrevSlide,
                    'aria-label': translate('aria-label.showPrevItem')
                }}
            />
            <Icon
                role="button"
                className={nextControlClassName}
                {...isNextControlActive && {
                    onClick: showNextSlide,
                    'aria-label': translate('aria-label.showNextItem')
                }}
            />
        </>
    );
};

export const CarouselControls = memo(CarouselControlsBare);
